// UserMenu.tsx
// Stateless helper for the sider footer user account block. Consumer owns
// user data, plan/usage data, dropdown items, and the sign-out callback.

import { useState } from 'react';
import { Avatar, Dropdown, Switch, Tag, Tooltip, Typography, theme as antTheme } from 'antd';
import { ChevronUp, Database, LogOut, Moon } from 'lucide-react';
import type { MenuProps } from 'antd';
import type { UserMenuProps } from './types';

const { Text } = Typography;

export function UserMenu({
  user,
  usage,
  menuItems = [],
  onSignOut,
  collapsed = false,
  roleBadge,
  mockDataToggle,
  themeToggle,
}: UserMenuProps) {
  const [open, setOpen] = useState(false);
  const { token } = antTheme.useToken();

  const initial = (user.firstName?.[0] ?? user.email?.[0] ?? 'U').toUpperCase();
  const fullName = user.firstName ? `${user.firstName} ${user.lastName ?? ''}`.trim() : user.email;
  // Single avatar element used in BOTH collapsed and expanded states so the
  // visible size / fill color stays identical across the toggle.
  const avatarEl = user.avatarUrl ? (
    <Avatar src={user.avatarUrl} size={32} />
  ) : (
    <span
      style={{
        width: 32,
        height: 32,
        borderRadius: '50%',
        background: token.colorFill,
        color: token.colorText,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 13,
        fontWeight: 600,
        flexShrink: 0,
      }}
    >
      {initial}
    </span>
  );
  const usageStrip = usage
    ? `${usage.plan} · ${usage.used}/${usage.limit}${usage.unit ? ` ${usage.unit}` : ''}`
    : null;
  const triggerLabel = `Account menu for ${fullName}`;

  // AntD MenuItems default `title` (the hover tooltip) to the label
  // content. Inside this dropdown the label is already visible — strip
  // the tooltip on every non-null item so we don't get redundant
  // "Settings" / "Billing" / etc. hover popups.
  const stripTitle = (item: NonNullable<MenuProps['items']>[number]) =>
    item && typeof item === 'object' ? { ...item, title: '' } : item;
  const items: MenuProps['items'] = [
    ...(menuItems ?? []).map(stripTitle),
    ...(menuItems && menuItems.length > 0 ? [{ type: 'divider' as const }] : []),
    {
      key: '__sign_out__',
      label: 'Sign out',
      icon: <LogOut size={14} />,
      danger: true,
      onClick: onSignOut,
      title: '',
    },
  ];

  // The entire footer area below the bottom menu is the trigger — no
  // bounded button shape. Hover bg fills the whole footer width.
  return (
    <Dropdown
      menu={{ items }}
      open={open}
      onOpenChange={setOpen}
      trigger={['click']}
      placement={collapsed ? 'topRight' : 'top'}
      // Trigger is itself inset 8px from the sider edges now (see its own
      // `margin: 8`), and `top`/`topRight` placement aligns the popover
      // flush to the trigger's own edge — so no extra x-offset is needed
      // for the popover to land with equal margins either side. -8 on y
      // opens a gap above the trigger instead of touching it.
      align={{ offset: [0, -8] }}
      overlayClassName="br-appshell-user-menu-popover"
      dropdownRender={(menu) => (
        // One surface for the whole popover — bg + border + radius live on
        // the outer wrapper. The inner sections (user info + AntD menu)
        // are transparent so they merge into a single card. The
        // box-shadow / inner bg overrides on .ant-dropdown-menu live in
        // app-shell.css.
        <div
          style={{
            boxSizing: 'border-box',
            // minWidth alone doesn't shrink this: it's a block-level div
            // that fills whatever space its parent (which shrink-wraps to
            // match this div) allows, so an explicit width is required.
            // 224 matches the trigger's own inset width (240px sider minus
            // an 8px margin each side, same as the nav highlight's default
            // inset) so the popover lands flush with the trigger's edges —
            // equal margins on both sides of the expanded sider.
            width: 224,
            background: token.colorBgElevated,
            border: `1px solid ${token.colorBorderSecondary}`,
            borderRadius: 8,
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              padding: '12px 14px',
              borderBottom: `1px solid ${token.colorBorderSecondary}`,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
              <div style={{ minWidth: 0, flex: 1 }}>
                <Text strong ellipsis={{ tooltip: fullName }} style={{ fontSize: 13, display: 'block', color: token.colorText }}>
                  {fullName}
                </Text>
                <Text ellipsis={{ tooltip: user.email }} style={{ fontSize: 11, display: 'block', marginTop: 2, color: token.colorTextSecondary }}>
                  {user.email}
                </Text>
              </div>
              {roleBadge && (
                <Tag color={roleBadge.color} style={{ margin: 0, flexShrink: 0 }}>
                  {roleBadge.label}
                </Tag>
              )}
            </div>
            {usageStrip && (
              <Text style={{ fontSize: 11, display: 'block', marginTop: 6, color: token.colorTextSecondary }}>
                {usageStrip}
              </Text>
            )}
            {mockDataToggle && (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginTop: 10,
                  paddingTop: 10,
                  borderTop: `1px solid ${token.colorBorderSecondary}`,
                }}
              >
                <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Database size={14} strokeWidth={1.75} color={token.colorTextSecondary} />
                  <Text style={{ fontSize: 13, color: token.colorText }}>
                    {mockDataToggle.label ?? 'Mock data'}
                  </Text>
                </span>
                <Switch
                  size="small"
                  checked={mockDataToggle.enabled}
                  onChange={(checked) => mockDataToggle.onChange(checked)}
                />
              </div>
            )}
            {themeToggle && (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginTop: mockDataToggle ? 8 : 10,
                  paddingTop: mockDataToggle ? 0 : 10,
                  borderTop: mockDataToggle ? 'none' : `1px solid ${token.colorBorderSecondary}`,
                }}
              >
                <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Moon size={14} strokeWidth={1.75} color={token.colorTextSecondary} />
                  <Text style={{ fontSize: 13, color: token.colorText }}>Dark Mode</Text>
                </span>
                <Switch
                  size="small"
                  checked={themeToggle.mode === 'dark'}
                  onChange={(checked) => themeToggle.onChange(checked ? 'dark' : 'light')}
                />
              </div>
            )}
          </div>
          {menu}
        </div>
      )}
    >
      <Tooltip title={collapsed ? fullName : ''} placement="right" align={{ offset: [4, 0] }}>
        <div
          role="button"
          tabIndex={0}
          aria-label={triggerLabel}
          className="br-appshell-user-menu-trigger"
          // Sits fully inside the sider (unlike the floating collapse
          // toggle, it doesn't straddle the content-area boundary, so no
          // z-index concerns) but is still, structurally, "outside" any
          // open Flyout's Drawer — exempt it so opening/using the account
          // menu doesn't dismiss an active flyout. The popover itself,
          // once open, is already exempt on its own: antd's Dropdown
          // portal carries the `.ant-dropdown` class, which FlyoutDrawer's
          // listener already whitelists.
          data-flyout-keep-open
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              setOpen(!open);
            }
          }}
          style={{
            // Inset rounded-rect button (matches nav items' 8px insets and
            // garnet-console's rail trigger) instead of a flat edge-to-edge
            // band — no divider line above it, transparent until hovered.
            height: 52,
            margin: 8,
            // Flex items default to `min-width: auto`, which floors their
            // size at the padding+border box — without this, a collapsed
            // width smaller than paddingLeft+paddingRight gets silently
            // ignored and the trigger overflows the collapsed sider.
            minWidth: 0,
            boxSizing: 'border-box',
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            // Collapsed mode has no visible text/chevron (avatar is
            // absolutely positioned, unaffected by padding) — zero it out
            // so the box can actually shrink to `width: 56` below.
            // paddingRight was 26 back when this box ran edge-to-edge; now
            // that it's already inset 8px via `margin`, that much padding
            // on top left the chevron looking far from the trigger's own
            // (visible-on-hover) right edge — tightened to sit close to it,
            // matching how nav items keep their chevron near their own row.
            paddingLeft: collapsed ? 0 : 50,
            paddingRight: collapsed ? 0 : 14,
            borderRadius: 8,
            cursor: 'pointer',
            color: token.colorText,
            overflow: 'hidden',
            ...(collapsed ? { width: 56 } : null),
          }}
        >
          {/* Avatar absolute at a single fixed offset — deliberately NOT
              conditional on `collapsed`. `margin: 8` above applies in both
              states, so the box's own left edge already sits at the same
              sider-x (8) either way; one constant `left` here means the
              avatar's absolute position doesn't jump when the sider
              collapses/expands, only the text+chevron beside it fade in
              and out. left:12 + half the 32px avatar = sider-x 36, the
              center of the 72px collapsed rail (matching nav icons). */}
          <span
            style={{
              position: 'absolute',
              left: 12,
              top: '50%',
              transform: 'translateY(-50%)',
            }}
          >
            {avatarEl}
          </span>
          <span
            style={{
              minWidth: 0,
              flex: 1,
              maxWidth: collapsed ? 0 : 200,
              opacity: collapsed ? 0 : 1,
              overflow: 'hidden',
              transition: 'max-width 0.2s ease, opacity 0.2s ease',
            }}
          >
            <Text
              style={{
                color: token.colorText,
                fontSize: 13,
                display: 'block',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {fullName}
            </Text>
            {usageStrip && (
              <Text
                style={{
                  color: token.colorTextTertiary,
                  fontSize: 11,
                  display: 'block',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {usageStrip}
              </Text>
            )}
          </span>
          <span
            aria-hidden="true"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              flexShrink: 0,
              marginLeft: 'auto',
              maxWidth: collapsed ? 0 : 14,
              opacity: collapsed ? 0 : 1,
              overflow: 'hidden',
              transition: 'max-width 0.2s ease, opacity 0.2s ease',
            }}
          >
            <ChevronUp
              size={14}
              color={token.colorTextTertiary}
              style={{
                transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.15s ease',
              }}
            />
          </span>
        </div>
      </Tooltip>
    </Dropdown>
  );
}
